import { useEffect, useState, type ReactNode } from 'react';
import type { AuditEvent, NotificationLog } from '@ww/shared';
import { api, type SmsTestResponse } from '../api';
import { PageHeader } from '../components/ui';
import { useLive } from '../hooks/useLive';
import { I, Icon } from '../lib/icons';
import {
  COMPONENT_KEYS,
  COMPONENT_LABELS,
  COMPONENT_NAMES,
  NOTIFICATION_COLORS,
  NOTIFICATION_LABELS,
  componentColor,
  errorMessage,
  isAdmin,
  modeStyle,
} from '../lib/status';
import { fmtAgo, fmtDateTime, fmtInterval, fmtRelative, useNow } from '../lib/time';

/**
 * Build versions of the two halves of the app. They are produced by separate
 * images, so a mismatch means a partial deploy or a stale browser cache.
 */
function BuildVersions({ apiVersion }: { apiVersion?: string }) {
  const web = __APP_VERSION__;
  const mismatch = !!apiVersion && apiVersion !== 'unknown' && apiVersion !== web;
  return (
    <span className="build-versions" title="Running build versions">
      <span className="mono">web v{web}</span>
      <span aria-hidden="true">·</span>
      <span className="mono">api v{apiVersion ?? '—'}</span>
      {mismatch && (
        <span className="build-mismatch" role="status">
          version mismatch
        </span>
      )}
    </span>
  );
}

function Metric({ label, value, color }: { label: string; value: ReactNode; color?: string }) {
  return (
    <div className="panel" style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <span className="cond" style={{ fontSize: 28, fontWeight: 700, color, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {value}
      </span>
      <span style={{ fontSize: 13, color: '#9fb0c8' }}>{label}</span>
    </div>
  );
}

export default function Health() {
  const { me, health, tenant, connected, refetchHealth, lastUpdated } = useLive();
  const admin = isAdmin(me?.user.role);
  const now = useNow(1000);
  const tz = tenant?.timezone;
  const [notifs, setNotifs] = useState<NotificationLog[] | null>(null);
  const [notifErr, setNotifErr] = useState<string | null>(null);
  const [audit, setAudit] = useState<AuditEvent[] | null>(null);
  const [auditErr, setAuditErr] = useState<string | null>(null);
  const [sms, setSms] = useState<SmsTestResponse | null>(null);
  const [smsErr, setSmsErr] = useState<string | null>(null);
  const [smsBusy, setSmsBusy] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    void refetchHealth();
  }, [refetchHealth]);

  useEffect(() => {
    let cancelled = false;
    api
      .notifications(50)
      .then((l) => {
        if (!cancelled) {
          setNotifs(Array.isArray(l) ? l : []);
          setNotifErr(null);
        }
      })
      .catch((e) => !cancelled && setNotifErr(errorMessage(e)));
    if (admin) {
      api
        .audit(100)
        .then((l) => {
          if (!cancelled) {
            setAudit(Array.isArray(l) ? l : []);
            setAuditErr(null);
          }
        })
        .catch((e) => !cancelled && setAuditErr(errorMessage(e)));
    }
    return () => {
      cancelled = true;
    };
  }, [admin, lastUpdated, reload]);

  const p = health?.polling ?? null;
  const ms = modeStyle(p?.mode);
  const c = health?.components;

  const pollRows: [string, ReactNode][] = p
    ? [
        ['Mode', <span style={{ color: ms.color, fontWeight: 600 }}>{ms.name}</span>],
        ['Reason', p.modeReason || '—'],
        ['Interval', fmtInterval(p.intervalMs)],
        ['Mode since', `${fmtDateTime(p.modeSince, tz, true)} (${fmtAgo(p.modeSince, now)})`],
        ['Recovery until', p.recoveryUntil ? `${fmtDateTime(p.recoveryUntil, tz, true)} (${fmtRelative(p.recoveryUntil, now)})` : '—'],
        ['Last poll', p.lastPollAt ? `${fmtDateTime(p.lastPollAt, tz, true)} (${fmtAgo(p.lastPollAt, now)})` : '—'],
        [
          'Last successful poll',
          p.lastPollSuccessAt ? `${fmtDateTime(p.lastPollSuccessAt, tz, true)} (${fmtAgo(p.lastPollSuccessAt, now)})` : '—',
        ],
        ['Next poll', p.nextPollAt ? `${fmtDateTime(p.nextPollAt, tz, true)} (${fmtRelative(p.nextPollAt, now)})` : '—'],
        ['Last poll latency', p.lastPollLatencyMs !== null && p.lastPollLatencyMs !== undefined ? `${p.lastPollLatencyMs} ms` : '—'],
        [
          'Last poll error',
          p.lastPollError ? <span style={{ color: '#ff8f93', overflowWrap: 'anywhere' }}>{p.lastPollError}</span> : 'None',
        ],
        ['Active alerts (feed)', String(p.activeAlertCount ?? '—')],
        ['Poll count', String(p.pollCount ?? '—')],
        ['Upstream requests', String(p.upstreamRequestCount ?? '—')],
        ['Worker heartbeat', p.heartbeatAt ? `${fmtAgo(p.heartbeatAt, now)}` : '—'],
      ]
    : [];

  return (
    <div className="page page-scroll" style={{ gap: 14 }}>
      <PageHeader title="System health" sub="All upstream requests are centralized. Browsers never poll NOAA/NWS directly.">
        <BuildVersions apiVersion={health?.version} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {sms && (
            <span role="status" style={{ fontSize: 14, color: sms.result === 'failed' ? '#ff8f93' : '#6fdc98' }}>
              Test SMS: <strong>{sms.result}</strong>
              {sms.detail ? ` · ${sms.detail}` : ''}
            </span>
          )}
          {smsErr && (
            <span role="alert" style={{ fontSize: 14, color: '#ff8f93' }}>
              {smsErr}
            </span>
          )}
          <button
            type="button"
            className="btn btn-ghost hov"
            onClick={() => {
              void refetchHealth();
              setReload((r) => r + 1);
            }}
          >
            <Icon d={I.refresh} size={16} /> Refresh
          </button>
          {admin && (
            <button
              type="button"
              className="btn btn-secondary hov"
              disabled={smsBusy}
              onClick={async () => {
                setSmsBusy(true);
                setSms(null);
                setSmsErr(null);
                try {
                  setSms(await api.smsTest());
                  setReload((r) => r + 1);
                } catch (e) {
                  setSmsErr(errorMessage(e));
                } finally {
                  setSmsBusy(false);
                }
              }}
            >
              {smsBusy ? 'Sending…' : 'Send test SMS'}
            </button>
          )}
        </div>
      </PageHeader>

      {health?.stale && (
        <div className="err-box" style={{ background: '#4a3410', borderColor: '#9a6d22', color: '#ffe2b8' }}>
          NWS alert feed is STALE — last successful poll {fmtAgo(p?.lastPollSuccessAt, now)}.
        </div>
      )}

      <div className="cards-4" style={{ flexShrink: 0 }}>
        {COMPONENT_KEYS.map((key) => {
          const comp = c?.[key];
          const status = comp?.status ?? 'unknown';
          const color = componentColor(status);
          return (
            <section key={key} className="panel" style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="dot" style={{ width: 10, height: 10, background: color }} />
                <span style={{ fontSize: 15, fontWeight: 600, flexGrow: 1 }}>{COMPONENT_NAMES[key]}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color }}>{COMPONENT_LABELS[status]}</span>
              </span>
              <span style={{ fontSize: 13, color: '#9fb0c8', lineHeight: 1.35, overflowWrap: 'anywhere' }}>
                {comp?.detail || (health ? 'No detail' : 'Loading…')}
              </span>
              <span style={{ fontSize: 12.5, color: '#8193ad' }}>
                Last success: {comp?.lastSuccess ? `${fmtDateTime(comp.lastSuccess, tz, true)} (${fmtAgo(comp.lastSuccess, now)})` : '—'}
                {comp?.latencyMs !== null && comp?.latencyMs !== undefined ? ` · ${comp.latencyMs} ms` : ''}
              </span>
              {key === 'realtime' && c?.realtime && (
                <span style={{ fontSize: 12.5, color: '#8193ad' }}>{c.realtime.connectedClients ?? 0} browsers connected</span>
              )}
            </section>
          );
        })}
        <section className="panel" style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="dot" style={{ width: 10, height: 10, background: connected ? '#3fb950' : '#ffb547' }} />
            <span style={{ fontSize: 15, fontWeight: 600, flexGrow: 1 }}>This browser</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: connected ? '#3fb950' : '#ffb547' }}>
              {connected ? 'Live' : 'Reconnecting'}
            </span>
          </span>
          <span style={{ fontSize: 13, color: '#9fb0c8', lineHeight: 1.35 }}>
            Server-sent events {connected ? 'connected' : 'disconnected; falling back to 60 s refresh'}.
          </span>
        </section>
      </div>

      <div className="metric-strip" style={{ gap: 12, flexShrink: 0 }}>
        <Metric label="Overall" value={COMPONENT_LABELS[health?.overall ?? 'unknown']} color={componentColor(health?.overall)} />
        <Metric label="Polling mode" value={p ? ms.name.replace(' mode', '') : '—'} color={ms.color} />
        <Metric label="Last poll latency" value={p?.lastPollLatencyMs !== null && p?.lastPollLatencyMs !== undefined ? `${p.lastPollLatencyMs} ms` : '—'} />
        <Metric label="Upstream requests" value={p?.upstreamRequestCount ?? '—'} />
        <Metric label="Connected browsers" value={c?.realtime?.connectedClients ?? '—'} />
      </div>

      <section className="panel" style={{ flexShrink: 0 }}>
        <div className="panel-head">
          <span className="lbl">NWS polling</span>
        </div>
        {!p && <div className="empty">No worker status yet. Is the ingestion worker running?</div>}
        {p && (
          <div className="grid-2" style={{ gap: 0, columnGap: 24 }}>
            {pollRows.map(([k, v]) => (
              <div
                key={k}
                style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '9px 16px', borderBottom: '1px solid #18253c', fontSize: 14 }}
              >
                <span style={{ color: '#9fb0c8', flexShrink: 0 }}>{k}</span>
                <span style={{ textAlign: 'right', minWidth: 0 }}>{v}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <div style={{ display: 'grid', gridTemplateColumns: admin ? 'minmax(0, 1.3fr) minmax(0, 1fr)' : '1fr', gap: 14, alignItems: 'start' }}>
        <section className="panel" style={{ overflow: 'hidden' }}>
          <div className="panel-head">
            <span className="lbl" style={{ flexGrow: 1 }}>
              Notification log
            </span>
            <span className="mono muted" style={{ fontSize: 12.5 }}>
              {notifs ? `${notifs.length} recent` : ''}
            </span>
          </div>
          {notifErr && <div className="err-box" style={{ margin: 12 }}>{notifErr}</div>}
          <div className="scroll" style={{ maxHeight: 520 }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Event</th>
                  <th>Recipients</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {!notifs && !notifErr && (
                  <tr>
                    <td colSpan={4} className="muted">
                      Loading…
                    </td>
                  </tr>
                )}
                {notifs && notifs.length === 0 && (
                  <tr>
                    <td colSpan={4} className="muted" style={{ textAlign: 'center', padding: 24 }}>
                      No notifications yet.
                    </td>
                  </tr>
                )}
                {(notifs ?? []).map((n) => (
                  <tr key={n._id}>
                    <td className="mono muted" style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>
                      {fmtDateTime(n.sentAt, tz, true)}
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{n.payload?.eventType || (n.alertId === 'test' ? 'Test message' : n.alertId)}</div>
                      <div className="muted" style={{ overflowWrap: 'anywhere' }}>
                        {n.payload?.message || n.reason}
                      </div>
                    </td>
                    <td className="muted">{(n.recipientGroup ?? []).join(', ') || '—'}</td>
                    <td style={{ color: NOTIFICATION_COLORS[n.result] ?? '#9fb0c8', whiteSpace: 'nowrap' }}>
                      {NOTIFICATION_LABELS[n.result] ?? n.result}
                      {n.reason && n.payload?.message ? <div className="muted" style={{ whiteSpace: 'normal' }}>{n.reason}</div> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {admin && (
          <section className="panel" style={{ overflow: 'hidden' }}>
            <div className="panel-head">
              <span className="lbl" style={{ flexGrow: 1 }}>
                Audit trail
              </span>
              <span className="mono muted" style={{ fontSize: 12.5 }}>
                {audit ? `${audit.length} recent` : ''}
              </span>
            </div>
            {auditErr && <div className="err-box" style={{ margin: 12 }}>{auditErr}</div>}
            <div className="scroll" style={{ maxHeight: 520 }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Change</th>
                  </tr>
                </thead>
                <tbody>
                  {!audit && !auditErr && (
                    <tr>
                      <td colSpan={2} className="muted">
                        Loading…
                      </td>
                    </tr>
                  )}
                  {audit && audit.length === 0 && (
                    <tr>
                      <td colSpan={2} className="muted" style={{ textAlign: 'center', padding: 24 }}>
                        No audit events.
                      </td>
                    </tr>
                  )}
                  {(audit ?? []).map((a) => (
                    <tr key={a._id}>
                      <td className="mono muted" style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>
                        {fmtDateTime(a.timestamp, tz)}
                      </td>
                      <td>
                        <div>
                          <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{a.action}</span> ·{' '}
                          {a.entityType.replace(/_/g, ' ')}
                          {a.summary ? `: ${a.summary}` : ''}
                        </div>
                        <div className="muted">{a.actorName}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
